import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Layers } from 'lucide-react';
import { useState } from 'react';

import type {
  CanvasCapture,
  ResearchRoom,
  ResearchRoomView,
} from '@repo/contracts';
import {
  MAX_ROOM_SCREENS,
  roomScreenIds,
} from '@repo/contracts/research-room-screens';

import { trackEvent } from '../analytics/posthog';
import { ApiError } from '../api/client';
import { roomKeys, updateRoomContext } from '../api/research-rooms';
import { CheckIcon } from '../lib/icons';

/**
 * Which of the board's screens the room is about, under the room's title —
 * and the way to narrow it.
 *
 * By default every screen on the board: pages and flow steps taken in the
 * room's browser join the room on their own, from the next question
 * (`roomScreenIds`). Unticking some here makes the room about just those
 * left, in the board's order — the order they were walked in — until "Use
 * every screen" hands it back to the board. The screen the room began on is
 * always in, and first: the room is found by it.
 */
export function RoomScreensPicker({
  captures,
  disabled,
  room,
}: {
  /** Every screen on the board, in the board's order. */
  captures: CanvasCapture[];
  /** While the room is answering, or once it has ended. */
  disabled: boolean;
  room: ResearchRoom;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chosen = Boolean(room.contextSetAt);
  const current = roomScreenIds(room, captures);
  const [picked, setPicked] = useState<string[]>(current);
  const byId = new Map(captures.map((capture) => [capture.id, capture]));
  const first = byId.get(room.captureId);
  const ordered = [
    ...(first ? [first] : []),
    ...captures.filter((capture) => capture.id !== room.captureId),
  ];

  const save = useMutation({
    mutationFn: (captureIds: string[] | 'all') =>
      captureIds === 'all'
        ? updateRoomContext(room.id, [room.captureId], { auto: true })
        : updateRoomContext(room.id, captureIds),
    onError: (err) => {
      setError(
        err instanceof ApiError && err.status === 409
          ? 'The room is answering. Try again once it finishes.'
          : "Couldn't change what the room looks at.",
      );
    },
    onSuccess: (view, captureIds) => {
      trackEvent('room_context_changed', {
        screens: captureIds === 'all' ? 'all' : captureIds.length,
        was: current.length,
      });
      queryClient.setQueryData<ResearchRoomView>(
        roomKeys.forCapture(room.captureId),
        view,
      );
      setOpen(false);
    },
  });

  // Only the screens still on the board count; one deleted since it joined
  // is already gone from the room's next round.
  const shown = current.filter((id) => byId.has(id));
  const label =
    shown.length <= 1
      ? `Looking at ${first?.name ?? 'one screen'}`
      : chosen
        ? `Looking at ${String(shown.length)} chosen screens`
        : `Looking at all ${String(shown.length)} screens`;
  const others = ordered.length > 1;

  const toggle = (id: string) => {
    setPicked((before) =>
      before.includes(id)
        ? before.filter((other) => other !== id)
        : [...before, id],
    );
  };
  // In the board's order, the room's first screen leading.
  const next = ordered
    .map((capture) => capture.id)
    .filter((id) => id === room.captureId || picked.includes(id));
  const changed =
    next.length !== shown.length || next.some((id, i) => shown[i] !== id);

  return (
    <div className="relative mt-2">
      <button
        aria-expanded={open}
        className="flex max-w-full cursor-pointer items-center gap-1.5 rounded-button py-0.5 text-left text-caption text-text-secondary transition hover:text-text-primary disabled:cursor-default disabled:opacity-60 disabled:hover:text-text-secondary"
        disabled={disabled || !others}
        onClick={() => {
          setPicked(current);
          setError(null);
          setOpen((was) => !was);
        }}
        title={
          others
            ? 'Choose which screens the room is about'
            : 'Capture more pages in the browser to add them here'
        }
        type="button"
      >
        <Layers aria-hidden="true" className="size-3.5 shrink-0" />
        <span className="truncate">{label}</span>
        {others && !disabled ? (
          <span className="shrink-0 font-medium text-text-primary">
            · Change
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          className="absolute top-full left-0 z-30 mt-2 w-80 rounded-card bg-surface p-1.5 shadow-popover ring-1 ring-hairline duration-150 animate-in fade-in slide-in-from-top-1"
          role="dialog"
          aria-label="Screens the room is about"
        >
          <p className="px-2.5 pt-1.5 pb-1 text-small-label text-text-muted uppercase">
            The room is about
          </p>
          <ul className="max-h-80 overflow-y-auto">
            {ordered.map((capture) => {
              const locked = capture.id === room.captureId;
              const on = locked || picked.includes(capture.id);
              const full = !on && next.length >= MAX_ROOM_SCREENS;
              return (
                <li key={capture.id}>
                  <button
                    aria-checked={on}
                    className="flex w-full cursor-pointer items-center gap-2.5 rounded-button px-2 py-1.5 text-left transition hover:bg-surface-hover disabled:cursor-default disabled:opacity-50 disabled:hover:bg-transparent"
                    disabled={locked || full}
                    onClick={() => {
                      toggle(capture.id);
                    }}
                    role="checkbox"
                    type="button"
                  >
                    <span
                      aria-hidden="true"
                      className={[
                        'flex size-4 shrink-0 items-center justify-center rounded-badge ring-1',
                        on
                          ? 'bg-ink text-white ring-ink'
                          : 'bg-surface ring-hairline-alt',
                      ].join(' ')}
                    >
                      {on ? <CheckIcon className="size-3" /> : null}
                    </span>
                    <img
                      alt=""
                      className="h-10 w-8 shrink-0 rounded-badge object-cover object-top ring-1 ring-hairline"
                      src={capture.imageUrl}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-ui text-text-primary">
                        {capture.name ?? 'Untitled screen'}
                      </span>
                      {locked ? (
                        <span className="block text-tag text-text-muted">
                          Where the room began
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="flex items-center gap-2 px-2.5 pt-2 pb-1.5">
            <p className="min-w-0 flex-1 text-tag leading-snug text-text-muted">
              {chosen ? (
                <button
                  className="mb-1 block cursor-pointer font-medium text-text-primary underline-offset-2 hover:underline"
                  disabled={save.isPending}
                  onClick={() => {
                    setError(null);
                    save.mutate('all');
                  }}
                  type="button"
                >
                  Use every screen
                </button>
              ) : (
                <span className="mb-1 block">
                  New screens join on their own.
                </span>
              )}
              Up to {MAX_ROOM_SCREENS}. Every screen is shown to everyone each
              round, so more screens cost more per question.
            </p>
            <button
              className="h-8 shrink-0 cursor-pointer rounded-button bg-ink px-3 text-ui font-semibold text-white transition hover:bg-ink/90 disabled:cursor-default disabled:opacity-50"
              disabled={!changed || save.isPending}
              onClick={() => {
                setError(null);
                save.mutate(next);
              }}
              type="button"
            >
              {save.isPending ? 'Saving…' : 'Update'}
            </button>
          </div>
          {error ? (
            <p
              className="px-2.5 pb-1.5 text-caption text-critical"
              role="alert"
            >
              {error}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
